import type { ServiceCode } from '@common/enums';
import { deviceEditSchema } from '@common/validation/device';
import { EditableCard } from '@frontend/components/elements/EditableCard';
import { useFormData } from '@frontend/contexts/support/application/hooks/useFormData';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IDevice } from './DeviceDetails';
import { DeviceInformationCardEditBody } from './DeviceInformationCardEditBody';
import { DeviceInformationCardReadBody } from './DeviceInformationCardReadBody';
import { DeviceInformationCardReadFooter } from './DeviceInformationCardReadFooter';

type DeviceInformationCardProps = {
    device: IDevice;
    onSave: () => Promise<void>;
    serviceCodes: ServiceCode[];
};

export type DeviceInformationCardEditFormData = {
    deviceTypeId: string | null;
    externalId: string | null;
    notes: string;
};

export function DeviceInformationCard(props: Readonly<DeviceInformationCardProps>) {
    const translate = getSelectedLanguage();

    const { device, onSave, serviceCodes } = props;

    const initialEditFormData = {
        deviceTypeId: device.deviceTypeId,
        externalId: device.externalId,
        notes: device.notes ?? '',
    };

    const abortSignal = useAbortSignal();
    const editFormData = useFormData<DeviceInformationCardEditFormData>(deviceEditSchema, initialEditFormData);

    return (
        <EditableCard
            editBody={
                <DeviceInformationCardEditBody
                    device={device}
                    formData={editFormData}
                />
            }
            onCancel={handleCancel}
            onSave={handleSave}
            readBody={
                <DeviceInformationCardReadBody
                    device={device}
                    serviceCodes={serviceCodes}
                />
            }
            readFooter={<DeviceInformationCardReadFooter device={device} />}
            title={translate.devices.details}
        />
    );

    async function handleSave() {
        if (!editFormData.hasChanges()) {
            return true;
        }

        const { hasErrors } = editFormData.validate();

        if (hasErrors) {
            return false;
        }

        await fetchJson({
            body: editFormData.formData,
            method: 'PATCH',
            signal: abortSignal,
            url: `/api/devices/${device.id}`,
        });

        await onSave();

        return true;
    }

    function handleCancel() {
        editFormData.reset();
    }
}
